// =============================================================================
// SIREPPAR - Wizard Candidatura, Paso 5: Declaración Jurada (Art. 72 y 76 CE)
// =============================================================================

import { useState } from 'react';
import toast from 'react-hot-toast';
import { candidatoService } from '../../../services/api';
import type { Candidato } from './CandidaturaWizardPage';

interface Props {
  candidato: Candidato;
  onSaved: () => void;
  onBack: () => void;
  onNext: () => void;
}

const INCISOS: Array<{ letra: string; texto: string }> = [
  { letra: 'a', texto: 'Personas excluidas del padrón por disposiciones legales vigentes.' },
  { letra: 'b', texto: 'Personal superior y subalterno de las Fuerzas Armadas de la Nación en actividad, o retirado llamado a prestar servicios.' },
  { letra: 'c', texto: 'Personal de fuerzas de seguridad nacional o provincial en actividad, o retirado llamado a prestar servicios.' },
  { letra: 'd', texto: 'Magistrados y funcionarios permanentes del Poder Judicial (Nación, provincias, CABA, Tribunales de Faltas Municipales).' },
  { letra: 'e', texto: 'Cargos directivos o apoderados de empresas concesionarias de servicios y obras públicas, o de empresas que exploten juegos de azar.' },
  { letra: 'f', texto: 'Personas con autos de procesamiento por genocidio, crímenes de lesa humanidad, crímenes de guerra u otras violaciones graves de los Derechos Humanos.' },
  { letra: 'g', texto: 'Personas condenadas por los crímenes descriptos en el inciso f), aun sin firmeza de la resolución.' },
  { letra: 'h', texto: 'Personas incluidas en el Registro de Alimentantes Morosos (RAM).' },
  { letra: 'i', texto: 'Personas condenadas por delitos dolosos de orden federal u ordinario con inhabilitación desde segunda instancia confirmatoria.' },
];

function declaracionInicial(candidato: Candidato) {
  const guardada = candidato.declaracionArt72;
  const incisosGuardados = guardada?.incisos || {};
  const incisos: Record<string, boolean> = {};
  INCISOS.forEach(i => { incisos[i.letra] = incisosGuardados[i.letra] === true; });
  return {
    incisos,
    candidaturaUnica: guardada?.candidaturaUnica === true,
    declaracionFinal: guardada?.declaracionFinal === true,
  };
}

export default function Paso5DeclaracionJurada({ candidato, onSaved, onBack, onNext }: Props) {
  const [declaracion, setDeclaracion] = useState(() => declaracionInicial(candidato));
  const [guardando, setGuardando] = useState(false);

  const guardar = async (datos: typeof declaracion, avisar = false) => {
    setGuardando(true);
    try {
      await candidatoService.guardarDeclaracionJurada(candidato.id, datos);
      onSaved();
      if (avisar) toast.success('Declaración jurada guardada');
    } catch {
      toast.error('No se pudo guardar la declaración jurada');
    } finally {
      setGuardando(false);
    }
  };

  const toggleInciso = (letra: string) => {
    const nuevo = { ...declaracion, incisos: { ...declaracion.incisos, [letra]: !declaracion.incisos[letra] } };
    setDeclaracion(nuevo);
    guardar(nuevo);
  };

  const marcarTodos = () => {
    const nuevo = { ...declaracion, incisos: Object.fromEntries(INCISOS.map(i => [i.letra, true])) };
    setDeclaracion(nuevo);
    guardar(nuevo);
  };

  const toggleCandidaturaUnica = () => {
    const nuevo = { ...declaracion, candidaturaUnica: !declaracion.candidaturaUnica };
    setDeclaracion(nuevo);
    guardar(nuevo);
  };

  const toggleDeclaracionFinal = () => {
    const nuevo = { ...declaracion, declaracionFinal: !declaracion.declaracionFinal };
    setDeclaracion(nuevo);
    guardar(nuevo);
  };

  const todosLosIncisos = INCISOS.every(i => declaracion.incisos[i.letra]);
  const puedeContinuar = todosLosIncisos && declaracion.candidaturaUnica && declaracion.declaracionFinal;

  return (
    <div className="space-y-6">
      <div className="bg-amber-50 border border-amber-200 rounded-lg p-4 text-sm text-amber-800">
        La persona candidata declara bajo juramento no encontrarse alcanzada por ninguno de los supuestos de inhabilidad previstos en el <strong>artículo 72 del Código Electoral</strong>. Marcá cada inciso confirmando que no aplica. La SEP realiza controles cruzados de oficio (RAP, RAM, Registro de Infractores).
      </div>

      <div className="bg-white rounded-xl shadow p-6">
      <div className="flex items-center justify-between mb-4">
          <h3 className="font-semibold text-gray-900">Supuestos de inhabilidad (Art. 72 CE)</h3>
          <button type="button" onClick={marcarTodos} className="text-xs text-blue-600 hover:text-blue-800 underline">
            Marcar todos
          </button>
        </div>
        <div className="border border-gray-100 rounded-lg divide-y divide-gray-100">
          {INCISOS.map(i => (
            <label key={i.letra} className="flex items-start gap-3 p-3 cursor-pointer hover:bg-gray-50">
              <input
                type="checkbox"
                checked={declaracion.incisos[i.letra] || false}
                onChange={() => toggleInciso(i.letra)}
                className="mt-0.5 w-4 h-4 accent-blue-600 flex-shrink-0"
              />
              <div className="text-sm">
                <span className="font-semibold text-blue-700 mr-1">{i.letra})</span>
                {i.texto}
              </div>
            </label>
          ))}
        </div>
      </div>

      <div className="bg-white rounded-xl shadow p-6">
        <h3 className="font-semibold text-gray-900 mb-4">Candidatura única (Art. 76 CE)</h3>
        <label className="flex items-start gap-3 p-3 bg-gray-50 rounded-lg cursor-pointer">
          <input
            type="checkbox"
            checked={declaracion.candidaturaUnica}
            onChange={toggleCandidaturaUnica}
            className="mt-0.5 w-4 h-4 accent-blue-600 flex-shrink-0"
          />
          <div className="text-sm">
            El candidato declara que <strong>no figura simultáneamente como candidato por otra agrupación política</strong> en esta u otra elección concurrente. En caso de alianzas, no coexisten listas de la alianza y de los partidos que la integran.
          </div>
        </label>
      </div>

      <div className="bg-white rounded-xl shadow p-6">
        <h3 className="font-semibold text-gray-900 mb-4">Declaración de veracidad</h3>
        <label className="flex items-start gap-3 p-3 bg-amber-50 rounded-lg cursor-pointer text-amber-800">
          <input
            type="checkbox"
            checked={declaracion.declaracionFinal}
            onChange={toggleDeclaracionFinal}
            className="mt-0.5 w-4 h-4 accent-blue-600 flex-shrink-0"
          />
          <div className="text-sm">
            <strong>Declaración final.</strong> El candidato y el apoderado declaran bajo juramento que todos los datos consignados en este formulario son veraces y que los documentos adjuntos son fidedignos. La falsedad de esta declaración configura falta electoral pasible de las sanciones del Título VIII del Código Electoral y responsabilidad penal según el artículo 293 del Código Penal.
          </div>
        </label>
      </div>

      <div className="flex items-center justify-between">
        <button onClick={onBack} className="px-4 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50 text-sm">← Volver</button>
        <button onClick={() => guardar(declaracion, true)} className="px-4 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50 text-sm">
          {guardando ? 'Guardando...' : 'Guardar borrador'}
        </button>
        <button
          onClick={onNext}
          disabled={!puedeContinuar}
          className="px-5 py-2.5 bg-blue-600 hover:bg-blue-700 disabled:opacity-40 disabled:cursor-not-allowed text-white rounded-lg text-sm font-medium"
        >
          Continuar a revisión final →
        </button>
      </div>
    </div>
  );
}
